/**
 * Replenishment page — three tabs that follow the lifecycle of a stock
 * top-up from a darkstore's perspective:
 *
 *   1. Levels   — current stock vs. min/reorder/max thresholds
 *   2. Staged   — warehouse baskets staged for transfer (gap analysis)
 *   3. Inbound  — transfers in flight + per-item racked progress
 *
 * Date / store come from the global topbar filter; in-page filters are
 * kept local so they don't pollute the session-wide state.
 */
import { Fragment, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Boxes,
  ChevronDown,
  ChevronRight,
  ChevronsUpDown,
  PackageCheck,
  Truck,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { ErrorState } from '@/components/ui/ErrorState';
import { EmptyState } from '@/components/ui/EmptyState';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { KPICard } from '@/components/charts/KPICard';
import { TaxonomyRail } from '@/components/inventory/TaxonomyRail';
import { useFilters, rangeISO } from '@/state/filters';
import {
  useInventoryTaxonomy,
  useReplenishmentBaskets,
  useReplenishmentLevels,
  useReplenishmentTransfers,
  type ReplenishmentLevelsFilters,
  type ReplenishmentBasketsFilters,
  type ReplenishmentTransfersFilters,
  type TransferStatusFilter,
} from '@/api/bi';
import type {
  ReplenishmentLevelStatus,
  TransferItemStatus,
} from '@/api/bi/types';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';

const PAGE_SIZE = 50;
const TRANSFER_PAGE_SIZE = 25;
const BASKETS_PAGE_SIZE = 100;

const STATUS_LABEL: Record<ReplenishmentLevelStatus, string> = {
  out: 'Out of stock',
  low: 'Below min',
  below_reorder: 'Below reorder',
  over_max: 'Over max',
  ok: 'Healthy',
};

const STATUS_TONE: Record<ReplenishmentLevelStatus, 'danger' | 'warning' | 'info' | 'success'> = {
  out: 'danger',
  low: 'danger',
  below_reorder: 'warning',
  over_max: 'info',
  ok: 'success',
};

const STATUS_OPTIONS: Array<{ value: 'all' | ReplenishmentLevelStatus; label: string }> = [
  { value: 'all', label: 'All statuses' },
  { value: 'out', label: 'Out of stock' },
  { value: 'low', label: 'Below min' },
  { value: 'below_reorder', label: 'Below reorder' },
  { value: 'over_max', label: 'Over max' },
  { value: 'ok', label: 'Healthy only' },
];

const TRANSFER_STATUS_OPTIONS: Array<{ value: TransferStatusFilter; label: string }> = [
  { value: 'all', label: 'All (excl. drafts)' },
  { value: 'in_transit', label: 'In transit' },
  { value: 'received', label: 'Received' },
  { value: 'partially_verified', label: 'Partially verified' },
  { value: 'accepted', label: 'Accepted (racked)' },
  { value: 'packed', label: 'Packed' },
];

const TRANSFER_STATUS_TONE: Record<string, 'success' | 'warning' | 'info' | 'neutral' | 'danger'> = {
  pending: 'neutral',
  packed: 'info',
  in_transit: 'info',
  received: 'warning',
  partially_verified: 'warning',
  accepted: 'success',
};

const ITEM_STATUS_LABEL: Record<TransferItemStatus, string> = {
  in_transit: 'In transit',
  received_unracked: 'Received · not racked',
  racked: 'Racked',
};

const ITEM_STATUS_TONE: Record<TransferItemStatus, 'info' | 'warning' | 'success'> = {
  in_transit: 'info',
  received_unracked: 'warning',
  racked: 'success',
};

// Local-only filter for the "Need vs. staged" table. The endpoint returns
// every (product, destination) row that's below its reorder trigger; this
// status carves that set into actionable buckets without an extra fetch.
type BasketStatusFilter =
  | 'all'
  | 'gap'
  | 'fully_staged'
  | 'unstaged'
  | 'waiting'
  | 'moving';

const BASKET_STATUS_OPTIONS: Array<{ value: BasketStatusFilter; label: string }> = [
  { value: 'all', label: 'All rows' },
  { value: 'gap', label: 'With gap' },
  { value: 'fully_staged', label: 'Fully staged' },
  { value: 'unstaged', label: 'Not staged' },
  { value: 'waiting', label: 'Has waiting baskets' },
  { value: 'moving', label: 'On vehicle' },
];

type BasketSortKey =
  | 'source'
  | 'destination'
  | 'product'
  | 'source_stock'
  | 'needed'
  | 'basketed'
  | 'gap'
  | 'basket_count'
  | 'oldest';

type SortDir = 'asc' | 'desc';

interface BasketSort {
  key: BasketSortKey;
  dir: SortDir;
}

const DEFAULT_BASKET_SORT: BasketSort = { key: 'gap', dir: 'desc' };


export function ReplenishmentPage() {
  const { range, storeId, storeIds, timeRangeMode } = useFilters();
  const iso = useMemo(() => rangeISO(range, timeRangeMode), [range, timeRangeMode]);

  const [vendorIds, setVendorIds] = useState<number[]>([]);
  const [brandIds, setBrandIds] = useState<number[]>([]);
  const [categoryGroupIds, setCategoryGroupIds] = useState<number[]>([]);
  const [categoryIds, setCategoryIds] = useState<number[]>([]);
  const [subcategoryIds, setSubcategoryIds] = useState<number[]>([]);
  const [q, setQ] = useState('');

  const [levelsStatus, setLevelsStatus] = useState<'all' | ReplenishmentLevelStatus>('all');
  const [levelsPage, setLevelsPage] = useState(1);

  const [basketsStatus, setBasketsStatus] = useState<BasketStatusFilter>('all');
  const [basketsSort, setBasketsSort] = useState<BasketSort>(DEFAULT_BASKET_SORT);
  const [basketsPage, setBasketsPage] = useState(1);

  const [transferStatus, setTransferStatus] = useState<TransferStatusFilter>('all');
  const [transferPage, setTransferPage] = useState(1);

  const taxonomy = useInventoryTaxonomy();

  const taxonomyFilters = {
    vendorIds,
    brandIds,
    categoryGroupIds,
    categoryIds,
    subcategoryIds,
    q: q.trim() || undefined,
  };
  const storeFilters = { storeId, storeIds };

  const resetPages = () => {
    setLevelsPage(1);
    setBasketsPage(1);
    setTransferPage(1);
  };

  const levelsFilters: ReplenishmentLevelsFilters = {
    ...storeFilters,
    ...taxonomyFilters,
    status: levelsStatus,
    page: levelsPage,
    pageSize: PAGE_SIZE,
  };
  const basketsFilters: ReplenishmentBasketsFilters = {
    ...storeFilters,
    ...taxonomyFilters,
  };
  const transfersFilters: ReplenishmentTransfersFilters = {
    start: iso.start,
    end: iso.end,
    storeId,
    storeIds,
    status: transferStatus,
    page: transferPage,
    pageSize: TRANSFER_PAGE_SIZE,
  };

  const levels = useReplenishmentLevels(levelsFilters);
  const baskets = useReplenishmentBaskets(basketsFilters);
  const transfers = useReplenishmentTransfers(transfersFilters);

  // Mirror the row-level filter the BasketsTable applies so the "N rows"
  // badge in the card header reflects what the user can actually page through.
  const filteredBasketsCount = useMemo(() => {
    if (!baskets.data) return 0;
    return baskets.data.rows.filter((r) => basketRowMatches(r, basketsStatus)).length;
  }, [baskets.data, basketsStatus]);

  return (
    <>
      <PageHeader
        eyebrow="Supply"
        title="Replenishment"
        description="Per-store stock vs. min/max/reorder, warehouse staging gaps, and inbound transfer racking progress."
      />

      <TaxonomyRail
        taxonomy={taxonomy.data}
        taxonomyLoading={taxonomy.isLoading}
        vendorIds={vendorIds}
        onVendorIds={(v) => { setVendorIds(v); resetPages(); }}
        brandIds={brandIds}
        onBrandIds={(v) => { setBrandIds(v); resetPages(); }}
        categoryGroupIds={categoryGroupIds}
        onCategoryGroupIds={(v) => { setCategoryGroupIds(v); resetPages(); }}
        categoryIds={categoryIds}
        onCategoryIds={(v) => { setCategoryIds(v); resetPages(); }}
        subcategoryIds={subcategoryIds}
        onSubcategoryIds={(v) => { setSubcategoryIds(v); resetPages(); }}
        q={q}
        onQ={(v) => { setQ(v); resetPages(); }}
        onClear={() => {
          setVendorIds([]);
          setBrandIds([]);
          setCategoryGroupIds([]);
          setCategoryIds([]);
          setSubcategoryIds([]);
          setQ('');
          resetPages();
        }}
      />

      <Tabs defaultValue="levels">
        <TabsList className="mb-4">
          <TabsTrigger value="levels">Levels</TabsTrigger>
          <TabsTrigger value="staged">Staged in warehouse</TabsTrigger>
          <TabsTrigger value="inbound">Inbound to stores</TabsTrigger>
        </TabsList>

        <TabsContent value="levels" className="space-y-4 outline-none">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <KPICard
              label="Out of stock"
              value={levels.data ? fmt.number(levels.data.totals.out) : '—'}
              loading={levels.isLoading}
              goodWhenUp={false}
              hint="Rows where stock_quantity ≤ 0"
            />
            <KPICard
              label="Below min"
              value={levels.data ? fmt.number(levels.data.totals.low) : '—'}
              loading={levels.isLoading}
              goodWhenUp={false}
              hint="stock ≤ min_stock_quantity (urgent)"
            />
            <KPICard
              label="Below reorder"
              value={levels.data ? fmt.number(levels.data.totals.below_reorder) : '—'}
              loading={levels.isLoading}
              goodWhenUp={false}
              hint="stock ≤ replenishment trigger"
            />
            <KPICard
              label="Healthy"
              value={levels.data ? fmt.number(levels.data.totals.ok) : '—'}
              loading={levels.isLoading}
              hint="Within target band"
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Stock levels</CardTitle>
              <div className="flex items-center gap-2">
                {levels.data ? (
                  <Badge tone="neutral">{fmt.number(levels.data.total)} rows</Badge>
                ) : null}
                <Select
                  value={levelsStatus}
                  onValueChange={(v) => {
                    setLevelsStatus(v as 'all' | ReplenishmentLevelStatus);
                    setLevelsPage(1);
                  }}
                >
                  <SelectTrigger className="h-8 w-[180px] text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardBody className="px-0 pb-0">
              <LevelsTable
                data={levels.data}
                isLoading={levels.isLoading}
                isError={levels.isError}
                onRetry={() => void levels.refetch()}
                page={levelsPage}
                onPage={setLevelsPage}
              />
            </CardBody>
          </Card>
        </TabsContent>

        <TabsContent value="staged" className="space-y-4 outline-none">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <KPICard
              label="Units needed"
              value={baskets.data ? fmt.number(baskets.data.totals.needed_units) : '—'}
              loading={baskets.isLoading}
              hint="Sum of (max − stock) across destinations at or below their reorder trigger"
            />
            <KPICard
              label="Units staged"
              value={baskets.data ? fmt.number(baskets.data.totals.basketed_units) : '—'}
              loading={baskets.isLoading}
              hint={
                baskets.data
                  ? `${fmt.number(baskets.data.totals.basketed_units_waiting)} waiting · ${fmt.number(baskets.data.totals.basketed_units_moving)} on vehicle`
                  : 'Sum of destination reserved_quantity (units in warehouse baskets aimed at the store)'
              }
            />
            <KPICard
              label="Replenishment gap"
              value={baskets.data ? fmt.number(baskets.data.totals.gap_units) : '—'}
              loading={baskets.isLoading}
              goodWhenUp={false}
              hint="Needed − staged (clamped at 0; rows without a max are excluded from the total)"
            />
            <KPICard
              label="Stores at risk"
              value={baskets.data ? fmt.number(baskets.data.totals.dest_stores_at_risk) : '—'}
              loading={baskets.isLoading}
              goodWhenUp={false}
              hint="Distinct destination stores with a non-zero gap"
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Need vs. staged · per (product, destination)</CardTitle>
              <div className="flex items-center gap-2">
                {baskets.data ? (
                  <Badge tone="neutral">
                    {fmt.number(filteredBasketsCount)} rows
                  </Badge>
                ) : null}
                <Select
                  value={basketsStatus}
                  onValueChange={(v) => {
                    setBasketsStatus(v as BasketStatusFilter);
                    setBasketsPage(1);
                  }}
                >
                  <SelectTrigger className="h-8 w-[200px] text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BASKET_STATUS_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardBody className="px-0 pb-0">
              <BasketsTable
                data={baskets.data}
                isLoading={baskets.isLoading}
                isError={baskets.isError}
                onRetry={() => void baskets.refetch()}
                statusFilter={basketsStatus}
                sort={basketsSort}
                onSort={(s) => {
                  setBasketsSort(s);
                  setBasketsPage(1);
                }}
                page={basketsPage}
                onPage={setBasketsPage}
                pageSize={BASKETS_PAGE_SIZE}
              />
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Unattributed warehouse baskets</CardTitle>
              <div className="flex items-center gap-2">
                <span className="hidden text-[11px] text-muted-foreground md:inline">
                  Filled at main · no destination assigned
                </span>
                {baskets.data ? (
                  <Badge tone={baskets.data.totals.unattributed_units > 0 ? 'warning' : 'neutral'}>
                    {fmt.number(baskets.data.totals.unattributed_units)} units · {fmt.number(baskets.data.totals.unattributed_baskets)} baskets
                  </Badge>
                ) : null}
              </div>
            </CardHeader>
            <CardBody className="px-0 pb-0">
              <UnattributedTable
                rows={baskets.data?.unattributed ?? []}
                isLoading={baskets.isLoading}
                isError={baskets.isError}
                onRetry={() => void baskets.refetch()}
              />
            </CardBody>
          </Card>
        </TabsContent>

        <TabsContent value="inbound" className="space-y-4 outline-none">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <KPICard
              label="Transfers in transit"
              value={transfers.data ? fmt.number(transfers.data.totals.transfers_in_transit) : '—'}
              loading={transfers.isLoading}
              hint="Packed or on the road, not yet received"
            />
            <KPICard
              label="Received · not racked"
              value={transfers.data ? fmt.number(transfers.data.totals.transfers_received_unracked) : '—'}
              loading={transfers.isLoading}
              goodWhenUp={false}
              hint="Arrived at darkstore but waiting on rack scan"
            />
            <KPICard
              label="Fully racked"
              value={transfers.data ? fmt.number(transfers.data.totals.transfers_fully_racked) : '—'}
              loading={transfers.isLoading}
              hint="All items accepted — fully on the rack"
            />
            <KPICard
              label="Median time to rack"
              value={
                transfers.data?.totals.median_received_to_racked_hours != null
                  ? `${fmt.number(transfers.data.totals.median_received_to_racked_hours)} h`
                  : '—'
              }
              loading={transfers.isLoading}
              goodWhenUp={false}
              hint="received_at → accepted_at, accepted transfers only"
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Inbound transfers</CardTitle>
              <div className="flex items-center gap-2">
                {transfers.data ? (
                  <Badge tone="neutral">{fmt.number(transfers.data.total)} transfers</Badge>
                ) : null}
                <Select
                  value={transferStatus}
                  onValueChange={(v) => {
                    setTransferStatus(v as TransferStatusFilter);
                    setTransferPage(1);
                  }}
                >
                  <SelectTrigger className="h-8 w-[200px] text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TRANSFER_STATUS_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardBody className="px-0 pb-0">
              <TransfersTable
                data={transfers.data}
                isLoading={transfers.isLoading}
                isError={transfers.isError}
                onRetry={() => void transfers.refetch()}
                page={transferPage}
                onPage={setTransferPage}
              />
            </CardBody>
          </Card>
        </TabsContent>
      </Tabs>
    </>
  );
}

// ---------------------------------------------------------------------------
// Levels table
// ---------------------------------------------------------------------------

interface LevelsTableProps {
  data: ReturnType<typeof useReplenishmentLevels>['data'];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  page: number;
  onPage: (n: number) => void;
}

function LevelsTable({ data, isLoading, isError, onRetry, page, onPage }: LevelsTableProps) {
  if (isLoading) return <Skeleton className="m-5 h-48 w-[calc(100%-2.5rem)]" />;
  if (isError) return <div className="p-5"><ErrorState onRetry={onRetry} /></div>;
  if (!data || data.rows.length === 0) {
    return (
      <div className="p-5">
        <EmptyState icon={<Boxes className="h-5 w-5" />} title="No availability rows match these filters" />
      </div>
    );
  }
  const totalPages = Math.max(1, Math.ceil(data.total / data.page_size));
  return (
    <div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left">Store</th>
              <th className="px-3 py-2 text-left">Product</th>
              <th className="px-3 py-2 text-left">Rack</th>
              <th className="px-3 py-2 text-right">Stock</th>
              <th className="px-3 py-2 text-right">Reserved</th>
              <th className="px-3 py-2 text-right">Min</th>
              <th className="px-3 py-2 text-right">Reorder</th>
              <th className="px-3 py-2 text-right">Max</th>
              <th className="px-3 py-2 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row) => {
              const tone = STATUS_TONE[row.status];
              return (
                <tr key={row.id} className="border-t border-border align-top">
                  <td className="px-3 py-2">
                    <span className="font-medium">{row.store_name}</span>
                    {row.store_type ? (
                      <span className="ml-1 text-[10px] uppercase text-muted-foreground">
                        {row.store_type}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-3 py-2">
                    <span className="font-medium">{row.product_name}</span>
                    {row.subcategory_name || row.brand ? (
                      <div className="text-[11px] text-muted-foreground">
                        {row.subcategory_name}
                        {row.brand ? ` · ${row.brand}` : ''}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {row.rack_name || '—'}
                  </td>
                  <td className={cn(
                    'px-3 py-2 text-right kpi-num font-semibold',
                    row.status === 'out' && 'text-danger',
                    row.status === 'low' && 'text-danger',
                    row.status === 'over_max' && 'text-warning-soft',
                  )}>
                    {fmt.number(row.stock_quantity)}
                  </td>
                  <td className="px-3 py-2 text-right kpi-num text-muted-foreground">
                    {row.reserved_quantity ? fmt.number(row.reserved_quantity) : '—'}
                  </td>
                  <td className="px-3 py-2 text-right kpi-num text-muted-foreground">
                    {row.min_stock_quantity != null ? fmt.number(row.min_stock_quantity) : '—'}
                  </td>
                  <td className="px-3 py-2 text-right kpi-num text-muted-foreground">
                    {row.replenishment_stock_quantity != null
                      ? fmt.number(row.replenishment_stock_quantity)
                      : '—'}
                  </td>
                  <td className="px-3 py-2 text-right kpi-num text-muted-foreground">
                    {row.max_stock_quantity != null ? fmt.number(row.max_stock_quantity) : '—'}
                  </td>
                  <td className="px-3 py-2">
                    <Badge tone={tone}>{STATUS_LABEL[row.status]}</Badge>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Pager page={page} totalPages={totalPages} onPage={onPage} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Baskets table (need vs. staged)
// ---------------------------------------------------------------------------

interface BasketsTableProps {
  data: ReturnType<typeof useReplenishmentBaskets>['data'];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  statusFilter: BasketStatusFilter;
  sort: BasketSort;
  onSort: (s: BasketSort) => void;
  page: number;
  onPage: (n: number) => void;
  pageSize: number;
}

type BasketRow = NonNullable<ReturnType<typeof useReplenishmentBaskets>['data']>['rows'][number];

function basketRowMatches(row: BasketRow, f: BasketStatusFilter): boolean {
  switch (f) {
    case 'gap':
      // null gap (max unset) -> we can't say there's a gap.
      return row.gap != null && row.gap > 0;
    case 'fully_staged':
      // Same caveat: only count rows where the gap is known to be zero.
      return row.gap === 0 && row.basketed > 0;
    case 'unstaged':
      return row.basketed === 0;
    case 'waiting':
      return row.baskets_waiting > 0;
    case 'moving':
      return row.baskets_moving > 0;
    case 'all':
    default:
      return true;
  }
}

// Pull the column value used for sorting. Strings are lower-cased so the
// alphabetical sort isn't case-sensitive; nulls always sink to the bottom
// regardless of direction so an empty cell never bubbles to the top.
function basketSortValue(row: BasketRow, key: BasketSortKey): number | string | null {
  switch (key) {
    case 'source':
      return (row.source_store_name ?? '').toLowerCase();
    case 'destination':
      return (row.dest_store_name ?? '').toLowerCase();
    case 'product':
      return (row.product_name ?? '').toLowerCase();
    case 'source_stock':
      return row.source_stock;
    case 'needed':
      return row.needed;
    case 'basketed':
      return row.basketed;
    case 'gap':
      return row.gap;
    case 'basket_count':
      return row.basket_count;
    case 'oldest':
      return row.oldest_basket_age_hours;
  }
}

function compareBasketRows(a: BasketRow, b: BasketRow, sort: BasketSort): number {
  const av = basketSortValue(a, sort.key);
  const bv = basketSortValue(b, sort.key);
  if (av == null && bv == null) return 0;
  if (av == null) return 1;
  if (bv == null) return -1;
  const dir = sort.dir === 'asc' ? 1 : -1;
  if (av < bv) return -1 * dir;
  if (av > bv) return 1 * dir;
  return 0;
}

function BasketsTable({
  data,
  isLoading,
  isError,
  onRetry,
  statusFilter,
  sort,
  onSort,
  page,
  onPage,
  pageSize,
}: BasketsTableProps) {
  const rows = useMemo(() => {
    if (!data) return [] as BasketRow[];
    const filtered = data.rows.filter((r) => basketRowMatches(r, statusFilter));
    return [...filtered].sort((a, b) => compareBasketRows(a, b, sort));
  }, [data, statusFilter, sort]);

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  // Clamp the requested page so resetting filters can never leave us beyond
  // the new last page (e.g. after switching to a stricter status filter).
  const safePage = Math.min(Math.max(1, page), totalPages);
  const pageRows = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, safePage, pageSize]);

  if (isLoading) return <Skeleton className="m-5 h-48 w-[calc(100%-2.5rem)]" />;
  if (isError) return <div className="p-5"><ErrorState onRetry={onRetry} /></div>;
  if (!data || data.rows.length === 0) {
    return (
      <div className="p-5">
        <EmptyState
          icon={<PackageCheck className="h-5 w-5" />}
          title="Nothing currently below the reorder threshold"
        />
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="p-5">
        <EmptyState
          icon={<PackageCheck className="h-5 w-5" />}
          title="No rows match this filter"
          description="Loosen the status filter to see more (product, destination) pairs."
        />
      </div>
    );
  }

  const toggle = (key: BasketSortKey) => {
    if (sort.key === key) {
      onSort({ key, dir: sort.dir === 'asc' ? 'desc' : 'asc' });
    } else {
      // Numeric columns default to descending (worst-first); text columns
      // default to ascending so A → Z reads naturally.
      const numericDefault: BasketSortKey[] = [
        'source_stock', 'needed', 'basketed', 'gap', 'basket_count', 'oldest',
      ];
      onSort({ key, dir: numericDefault.includes(key) ? 'desc' : 'asc' });
    }
  };

  return (
    <div>
      <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
          <tr>
            <BasketSortHeader sortKey="source" sort={sort} onToggle={toggle} align="left">
              Source
            </BasketSortHeader>
            <BasketSortHeader sortKey="destination" sort={sort} onToggle={toggle} align="left">
              Destination
            </BasketSortHeader>
            <BasketSortHeader sortKey="product" sort={sort} onToggle={toggle} align="left">
              Product
            </BasketSortHeader>
            <BasketSortHeader sortKey="source_stock" sort={sort} onToggle={toggle} align="right">
              Source stock
            </BasketSortHeader>
            <BasketSortHeader sortKey="needed" sort={sort} onToggle={toggle} align="right">
              Needed
            </BasketSortHeader>
            <BasketSortHeader sortKey="basketed" sort={sort} onToggle={toggle} align="right">
              Staged
            </BasketSortHeader>
            <BasketSortHeader sortKey="gap" sort={sort} onToggle={toggle} align="right">
              Gap
            </BasketSortHeader>
            <BasketSortHeader sortKey="basket_count" sort={sort} onToggle={toggle} align="right">
              Baskets
            </BasketSortHeader>
            <th className="px-3 py-2 text-left">Status</th>
            <BasketSortHeader sortKey="oldest" sort={sort} onToggle={toggle} align="right">
              Oldest basket
            </BasketSortHeader>
          </tr>
        </thead>
        <tbody>
          {pageRows.map((row) => (
            <tr
              key={`${row.product_id}-${row.dest_store_id}`}
              className="border-t border-border align-top"
            >
              <td className="px-3 py-2 text-muted-foreground">
                {row.source_store_name ?? '—'}
              </td>
              <td className="px-3 py-2 font-medium">{row.dest_store_name}</td>
              <td className="px-3 py-2">
                <span className="font-medium">{row.product_name}</span>
                {row.brand ? (
                  <div className="text-[11px] text-muted-foreground">{row.brand}</div>
                ) : null}
              </td>
              <td
                className={cn(
                  'px-3 py-2 text-right kpi-num',
                  row.needed != null && row.source_stock < row.needed
                    ? 'text-warning-soft'
                    : 'text-muted-foreground',
                )}
              >
                {fmt.number(row.source_stock)}
              </td>
              <td className="px-3 py-2 text-right kpi-num">{fmt.number(row.needed)}</td>
              <td className="px-3 py-2 text-right kpi-num text-success">
                {row.basketed ? `+${fmt.number(row.basketed)}` : '—'}
              </td>
              <td
                className={cn(
                  'px-3 py-2 text-right kpi-num font-semibold',
                  row.gap == null
                    ? 'text-muted-foreground'
                    : row.gap > 0
                      ? 'text-danger'
                      : 'text-success',
                )}
              >
                {fmt.number(row.gap)}
              </td>
              <td className="px-3 py-2 text-right kpi-num text-muted-foreground">
                {row.basket_count ? fmt.number(row.basket_count) : '—'}
              </td>
              <td className="px-3 py-2">
                <div className="flex flex-wrap gap-1">
                  {row.baskets_waiting ? (
                    <Badge tone="warning">
                      {fmt.number(row.baskets_waiting)} waiting · {fmt.number(row.units_waiting)} u
                    </Badge>
                  ) : null}
                  {row.baskets_moving ? (
                    <Badge tone="info">
                      {fmt.number(row.baskets_moving)} on vehicle · {fmt.number(row.units_moving)} u
                    </Badge>
                  ) : null}
                  {!row.baskets_waiting && !row.baskets_moving ? (
                    <span className="text-xs text-muted-foreground">—</span>
                  ) : null}
                </div>
              </td>
              <td className="px-3 py-2 text-right text-xs text-muted-foreground">
                {row.oldest_basket_age_hours != null
                  ? `${fmt.number(row.oldest_basket_age_hours)} h`
                  : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      <Pager page={safePage} totalPages={totalPages} onPage={onPage} />
    </div>
  );
}

interface BasketSortHeaderProps {
  sortKey: BasketSortKey;
  sort: BasketSort;
  onToggle: (key: BasketSortKey) => void;
  align: 'left' | 'right';
  children: React.ReactNode;
}

function BasketSortHeader({ sortKey, sort, onToggle, align, children }: BasketSortHeaderProps) {
  const active = sort.key === sortKey;
  return (
    <th
      scope="col"
      className={cn(
        'cursor-pointer select-none px-3 py-2',
        align === 'right' ? 'text-right' : 'text-left',
        active ? 'text-foreground' : 'text-muted-foreground',
      )}
      onClick={() => onToggle(sortKey)}
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <span
        className={cn(
          'inline-flex items-center gap-1',
          align === 'right' ? 'flex-row-reverse' : 'flex-row',
        )}
      >
        {children}
        {active ? (
          <ChevronDown
            className={cn('h-3 w-3', sort.dir === 'asc' && 'rotate-180')}
            aria-hidden
          />
        ) : (
          <ChevronsUpDown className="h-3 w-3 opacity-50" aria-hidden />
        )}
      </span>
    </th>
  );
}

interface UnattributedTableProps {
  rows: NonNullable<ReturnType<typeof useReplenishmentBaskets>['data']>['unattributed'];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}

function UnattributedTable({ rows, isLoading, isError, onRetry }: UnattributedTableProps) {
  if (isLoading) return <Skeleton className="m-5 h-24 w-[calc(100%-2.5rem)]" />;
  if (isError) return <div className="p-5"><ErrorState onRetry={onRetry} /></div>;
  if (rows.length === 0) {
    return (
      <div className="p-5">
        <EmptyState
          icon={<AlertTriangle className="h-5 w-5" />}
          title="No unattributed warehouse baskets"
        />
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
          <tr>
            <th className="px-3 py-2 text-left">Product</th>
            <th className="px-3 py-2 text-right">Units</th>
            <th className="px-3 py-2 text-right">Baskets</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.product_id} className="border-t border-border">
              <td className="px-3 py-2">
                <span className="font-medium">{row.product_name}</span>
                {row.brand ? (
                  <div className="text-[11px] text-muted-foreground">{row.brand}</div>
                ) : null}
              </td>
              <td className="px-3 py-2 text-right kpi-num">{fmt.number(row.qty)}</td>
              <td className="px-3 py-2 text-right kpi-num text-muted-foreground">
                {fmt.number(row.basket_count)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Transfers table (with expandable per-item rows)
// ---------------------------------------------------------------------------

interface TransfersTableProps {
  data: ReturnType<typeof useReplenishmentTransfers>['data'];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  page: number;
  onPage: (n: number) => void;
}

function TransfersTable({ data, isLoading, isError, onRetry, page, onPage }: TransfersTableProps) {
  if (isLoading) return <Skeleton className="m-5 h-48 w-[calc(100%-2.5rem)]" />;
  if (isError) return <div className="p-5"><ErrorState onRetry={onRetry} /></div>;
  if (!data || data.rows.length === 0) {
    return (
      <div className="p-5">
        <EmptyState icon={<Truck className="h-5 w-5" />} title="No transfers in this window" />
      </div>
    );
  }
  const totalPages = Math.max(1, Math.ceil(data.total / data.page_size));
  return (
    <div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="w-6 px-3 py-2" aria-label="expand" />
              <th className="px-3 py-2 text-left">Identifier</th>
              <th className="px-3 py-2 text-left">From → To</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Created</th>
              <th className="px-3 py-2 text-right">Items</th>
              <th className="px-3 py-2 text-right">Requested</th>
              <th className="px-3 py-2 text-right">Received</th>
              <th className="px-3 py-2 text-right">Racked</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((t) => (
              <TransferRow key={t.transfer_id} transfer={t} />
            ))}
          </tbody>
        </table>
      </div>
      <Pager page={page} totalPages={totalPages} onPage={onPage} />
    </div>
  );
}

function TransferRow({
  transfer,
}: {
  transfer: NonNullable<ReturnType<typeof useReplenishmentTransfers>['data']>['rows'][number];
}) {
  const [open, setOpen] = useState(false);
  return (
    <Fragment>
      <tr className="border-t border-border align-top">
        <td className="px-3 py-2">
          <button
            type="button"
            className="rounded p-1 text-muted-foreground hover:bg-muted/40"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? 'Collapse items' : 'Expand items'}
          >
            <ChevronRight
              className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-90')}
            />
          </button>
        </td>
        <td className="px-3 py-2 font-medium">{transfer.identifier}</td>
        <td className="px-3 py-2">
          <span className="text-muted-foreground">{transfer.source_store.name ?? '—'}</span>
          <span className="mx-1 text-muted-foreground">→</span>
          <span className="font-medium">{transfer.destination_store.name ?? '—'}</span>
        </td>
        <td className="px-3 py-2">
          <Badge tone={TRANSFER_STATUS_TONE[transfer.status] ?? 'neutral'}>
            {transfer.status.replace('_', ' ')}
          </Badge>
        </td>
        <td className="whitespace-nowrap px-3 py-2 text-xs text-muted-foreground">
          {fmt.datetime(transfer.created_at)}
        </td>
        <td className="px-3 py-2 text-right kpi-num">{fmt.number(transfer.items_total)}</td>
        <td className="px-3 py-2 text-right kpi-num text-muted-foreground">
          {fmt.number(transfer.units_requested)}
        </td>
        <td className="px-3 py-2 text-right kpi-num">
          {fmt.number(transfer.units_received)}
        </td>
        <td className={cn(
          'px-3 py-2 text-right kpi-num font-semibold',
          transfer.units_racked === transfer.units_requested && transfer.units_requested > 0
            ? 'text-success'
            : transfer.units_racked === 0
              ? 'text-muted-foreground'
              : 'text-warning-soft',
        )}>
          {fmt.number(transfer.units_racked)}
        </td>
      </tr>
      {open ? (
        <tr className="border-t border-border bg-muted/20">
          <td />
          <td colSpan={8} className="px-3 py-2">
            <table className="min-w-full text-xs">
              <thead className="text-[10px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-2 py-1 text-left">Product</th>
                  <th className="px-2 py-1 text-right">Requested</th>
                  <th className="px-2 py-1 text-right">Received</th>
                  <th className="px-2 py-1 text-right">Racked</th>
                  <th className="px-2 py-1 text-left">Racked units · rack</th>
                  <th className="px-2 py-1 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {transfer.items.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-2 py-2 text-muted-foreground">
                      No items recorded.
                    </td>
                  </tr>
                ) : (
                  transfer.items.map((it) => (
                    <tr key={it.item_id} className="border-t border-border/60">
                      <td className="px-2 py-1">
                        <span className="font-medium">{it.product_name}</span>
                        {it.brand ? (
                          <span className="ml-1 text-muted-foreground">· {it.brand}</span>
                        ) : null}
                      </td>
                      <td className="px-2 py-1 text-right kpi-num text-muted-foreground">
                        {fmt.number(it.requested)}
                      </td>
                      <td className="px-2 py-1 text-right kpi-num">{fmt.number(it.received)}</td>
                      <td className="px-2 py-1 text-right kpi-num">{fmt.number(it.racked)}</td>
                      <td className="px-2 py-1 align-top text-muted-foreground">
                        {it.racked_units_detail.length === 0 ? (
                          <span className="text-[11px]">—</span>
                        ) : (
                          <ul className="list-inside list-disc space-y-0.5 text-[11px]">
                            {it.racked_units_detail.map((u) => (
                              <li key={u.tag}>
                                <span className="font-mono text-foreground">{u.tag}</span>
                                <span className="text-muted-foreground"> · </span>
                                <span>{u.rack_name ?? '—'}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>
                      <td className="px-2 py-1">
                        <Badge tone={ITEM_STATUS_TONE[it.item_status]}>
                          {ITEM_STATUS_LABEL[it.item_status]}
                        </Badge>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </td>
        </tr>
      ) : null}
    </Fragment>
  );
}

// ---------------------------------------------------------------------------
// Pager
// ---------------------------------------------------------------------------

function Pager({
  page,
  totalPages,
  onPage,
}: {
  page: number;
  totalPages: number;
  onPage: (n: number) => void;
}) {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between border-t border-border px-3 py-2 text-xs">
      <span className="text-muted-foreground">
        Page <span className="kpi-num">{page}</span> of <span className="kpi-num">{totalPages}</span>
      </span>
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
        >
          Prev
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={page >= totalPages}
          onClick={() => onPage(page + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
